/**
 * App-bar notifications bell (DNKADM-0047 U1).
 *
 * An `IconButton` (bell glyph) with an MUI `Badge` showing the actionable
 * attention count (the server's `totalElements`, or the loaded list length when it
 * sent no total — DNKADM-0243), placed in the AppBar Toolbar so it renders in BOTH
 * Front Desk and Configuration modes (the Toolbar always renders; only the nav
 * swaps per mode).
 *
 * The count + emphasis come from the shared `useNotifications()` polling context —
 * one source of truth for U1's badge and U2's popover. The badge is HIDDEN at
 * count 0 and FAILS SOFT (no badge, the bell still renders) on a load error.
 * Emphasis: any OPEN-ERROR rows badge `error` (red); otherwise the attention badge
 * is the warning color.
 *
 * Clicking the bell opens the popover, whose body is the `NotificationsPanel`
 * (U2) — the sorted list, status filter, and per-row Acknowledge.
 */
import { useState } from 'react';
import { Badge, IconButton, Popover, Tooltip } from '@mui/material';
import { Icon } from '@/theme';
import { useNotifications } from './useNotifications';
import { NotificationsPanel } from './NotificationsPanel';

export function NotificationsBell() {
  const { openCount, openErrorCount } = useNotifications();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);
  const hasError = openErrorCount > 0;
  // Accessible label reflects the attention count so screen-reader users hear it.
  const label =
    openCount > 0 ? `Notifications, ${openCount} needing attention` : 'Notifications';

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton
          aria-label={label}
          aria-haspopup="dialog"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          {/* `invisible` hides the badge at count 0 (no zero bubble); color emphasizes ERROR. */}
          <Badge
            badgeContent={openCount}
            invisible={openCount === 0}
            color={hasError ? 'error' : 'warning'}
            overlap="circular"
          >
            <Icon name="notifications" sx={{ fontSize: 22 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 400, maxWidth: '90vw' } } }}
      >
        {/* Remount the panel each open so it re-fetches a fresh filtered list. */}
        {open ? <NotificationsPanel onNavigate={() => setAnchorEl(null)} /> : null}
      </Popover>
    </>
  );
}
